import React, { useState, useEffect } from 'react';
import { useEditMode } from '../hooks/useEditMode';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Edit2, User, X, Save, Link as LinkIcon } from 'lucide-react';

interface EditableImageProps {
  section: string;
  imageKey: string;
  defaultSrc: string;
  alt: string;
  className?: string;
  onImageUpdate?: (url: string) => void;
}

export const EditableImage: React.FC<EditableImageProps> = ({
  section,
  imageKey,
  defaultSrc,
  alt,
  className = '',
  onImageUpdate
}) => {
  const { isEditMode } = useEditMode();
  const [isEditing, setIsEditing] = useState(false);
  const [imageUrl, setImageUrl] = useState(defaultSrc);
  const [tempUrl, setTempUrl] = useState(defaultSrc);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const docRef = doc(db, 'content', `${section}_${imageKey}`);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.value?.url) {
            setImageUrl(data.value.url);
            setTempUrl(data.value.url);
          }
        }
      } catch (error) {
        console.error('Error fetching image:', error);
      }
    };

    fetchImage();
  }, [section, imageKey]);

  const validateUrl = (url: string) => {
    if (!url) return false;
    try {
      new URL(url);
      return url.match(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i) !== null;
    } catch {
      return false;
    }
  };

  const checkImageExists = (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  const handleSave = async () => {
    if (!validateUrl(tempUrl)) {
      setError('Please enter a valid image URL (must end with .jpg, .jpeg, .png, .gif, or .webp)');
      return;
    }

    setIsSaving(true);
    setError(null);
    setIsImageLoading(true);

    try {
      // Check if image exists and can be loaded
      const imageExists = await checkImageExists(tempUrl);
      if (!imageExists) {
        throw new Error('Image could not be loaded. Please check the URL and try again.');
      }

      // Save to Firestore
      const docRef = doc(db, 'content', `${section}_${imageKey}`);
      await setDoc(docRef, {
        section,
        key: imageKey,
        value: { url: tempUrl },
        updatedAt: new Date()
      });

      setImageUrl(tempUrl);
      if (onImageUpdate) {
        onImageUpdate(tempUrl);
      }
      setIsEditing(false);
    } catch (error: any) {
      setError(error.message || 'Failed to save image. Please try again.');
    } finally {
      setIsSaving(false);
      setIsImageLoading(false);
    }
  };

  const handleImageError = () => {
    setError('Failed to load image. Please check the URL and try again.');
  };

  if (!isEditMode) {
    return imageUrl ? (
      <img 
        src={imageUrl} 
        alt={alt} 
        className={className}
        onError={handleImageError}
      />
    ) : (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <User className="h-16 w-16 text-gray-400" />
      </div>
    );
  }

  if (!isEditing) {
    return (
      <div
        className={`group relative cursor-pointer ${className}`}
        onClick={() => setIsEditing(true)}
      >
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={alt} 
            className={className}
            onError={handleImageError}
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-100">
            <div className="text-center">
              <User className="h-16 w-16 text-gray-400 mx-auto mb-2" />
              <span className="text-sm text-gray-500">Click to add image</span>
            </div>
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-dark/5 opacity-0 group-hover:opacity-100 transition-all duration-200">
          <div className="bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 flex items-center gap-2 shadow-lg">
            <Edit2 className="h-4 w-4" />
            <span className="text-sm font-medium">Edit image</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="border-2 border-dashed border-dark/20 rounded-lg p-4 bg-dark/5">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Image URL
            </label>
            <div className="relative">
              <input
                type="text"
                value={tempUrl}
                onChange={(e) => {
                  setTempUrl(e.target.value);
                  setError(null);
                }}
                placeholder="Enter image URL (e.g., https://example.com/image.jpg)"
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:ring-dark focus:border-dark"
              />
              <LinkIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              URL must end with .jpg, .jpeg, .png, .gif, or .webp
            </p>
          </div>

          {tempUrl && (
            <div className="relative aspect-w-3 aspect-h-4 bg-gray-100 rounded-md overflow-hidden">
              {isImageLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dark"></div>
                </div>
              )}
              <img
                src={tempUrl}
                alt="Preview"
                className="object-cover w-full h-full"
                onError={handleImageError}
                onLoad={() => setIsImageLoading(false)}
              />
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md border border-red-100">
              {error}
            </div>
          )}
        </div>
      </div>

      <div className="absolute right-0 mt-2 flex gap-2">
        <button
          onClick={() => {
            setIsEditing(false);
            setTempUrl(imageUrl);
            setError(null);
          }}
          className="p-1.5 text-red-600/60 hover:text-red-600 hover:bg-red-50 rounded-md"
          disabled={isSaving}
          title="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
        <button
          onClick={handleSave}
          className="p-1.5 text-green-600/60 hover:text-green-600 hover:bg-green-50 rounded-md"
          disabled={isSaving || !tempUrl || isImageLoading}
          title="Save"
        >
          <Save className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};